import React from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Stack,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import FactoryIcon from '@mui/icons-material/Factory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import InventoryIcon from "@mui/icons-material/Inventory"

const HeroSection = styled(Box)(({ theme }) => ({
    position: "relative",
    minHeight: "50vh",
    height: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    backgroundImage: `url(${process.env.PUBLIC_URL}/Yuva-2.jpg)`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    padding: theme.spacing(2, 4),
    marginBottom: theme.spacing(8),

    // Create the black overlay
    "::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)", // Adjust opacity here (0.5 = 50% black)
        zIndex: 1,
    },

    // Ensure content appears above the overlay
    "& > *": {
        position: "relative",
        zIndex: 2,
    },
}));    
  
const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  color: 'white',
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: 60,
    height: 60,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    background: theme.palette.background.paper,
    margin: "auto",  // Add this line
}));

function Yuvasahakar() {
     const theme = useTheme();
      const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    return (
        <Box>
          {/* Hero Section */}
          <HeroSection>
                <Box sx={{ textAlign: "center", maxWidth: { xs: 300, md: 800 } }}>
                    <IconWrapper>
                        <EmojiPeopleIcon color="primary" sx={{ fontSize: 30 }} />
                    </IconWrapper>
                    <Typography variant="h2" component="h1" gutterBottom>
                Yuva Sahakar
              </Typography>
              <Typography variant="h5">
                Empowering Youth Through Cooperative Entrepreneurship
              </Typography>
                </Box>
            </HeroSection>
    
          <Container maxWidth="lg" sx={{marginTop:"20px"}}>
            {/* Overview Section */}
            <Card sx={{ mb: 6, mt: 8, position: 'relative', zIndex: 1 }}>
              <CardContent>
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="h4" gutterBottom color="primary">
                      About Yuva Sahakar
                    </Typography>
                    <Typography variant="body1" paragraph>
                      The Yuva Sahakar Scheme is an initiative launched under the National Cooperative Development Corporation (NCDC), aimed at promoting and supporting cooperatives led by young entrepreneurs. The scheme is aligned with the vision of encouraging youth to participate in the cooperative movement and to foster innovation and sustainability within cooperative businesses.
                    </Typography>
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center">
                        <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                        <Typography>Support for youth-led cooperatives</Typography>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                        <Typography>Nationwide implementation</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      component="img"
                      src="/yuva-1.jpg"
                      alt="Yuva Sahakar Initiative"
                      sx={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: 2,
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
    
            {/* Highlights Section */}
            <Card sx={{ mb: 6, bgcolor: theme.palette.primary.main, color: 'white' }}>
              <CardContent>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={12}>
                    <Typography variant="h4" gutterBottom>
                      Highlights of the Scheme
                    </Typography>
                    <Typography paragraph>
                      Supporting the youth in becoming successful cooperative entrepreneurs through financial assistance and guidance.
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <StatsCard>
                          <Typography variant="h4">35</Typography>
                          <Typography variant="subtitle1">Maximum Age Limit</Typography>
                        </StatsCard>
                      </Grid>
                      <Grid item xs={6}>
                        <StatsCard>
                          <Typography variant="h4">50%</Typography>
                          <Typography variant="subtitle1">Youth Membership Required</Typography>
                        </StatsCard>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h5" gutterBottom color="primary">
                          Key Features
                        </Typography>
                        <Stack spacing={2}>
                          <Box display="flex" alignItems="center">
                            <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                            <Typography>Financial assistance at concessional rates</Typography>
                          </Box>
                          <Box display="flex" alignItems="center">
                            <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                            <Typography>Support for various sectors</Typography>
                          </Box>
                          <Box display="flex" alignItems="center">
                            <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                            <Typography>Priority to underserved areas</Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
    
            {/* Implementation Areas Section */}
            <Card sx={{ mb: 6 }}>
              <CardContent>
                <Typography variant="h4" align="center" gutterBottom color="primary">
                  Implementation Areas
                </Typography>
                <Typography align="center" paragraph sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
                  The scheme covers various sectors to promote comprehensive youth development in cooperatives.
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <IconWrapper sx={{margin:"auto"}}>
                          <AgricultureIcon color="primary" sx={{ fontSize: 30 }} />
                        </IconWrapper>
                        <Typography variant="h6" gutterBottom>
                          Agriculture
                        </Typography>
                        <Typography>
                          Supporting farming and agri-business cooperatives
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <IconWrapper sx={{margin:"auto"}}>
                          <LocalMallIcon color="primary" sx={{ fontSize: 30 }} />
                        </IconWrapper>
                        <Typography variant="h6" gutterBottom>
                          Textiles
                        </Typography>
                        <Typography>
                          Promoting textile and handicraft cooperatives
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <IconWrapper sx={{margin:"auto"}}>
                          <InventoryIcon color="primary" sx={{ fontSize: 30 }} />
                        </IconWrapper>
                        <Typography variant="h6" gutterBottom>
                          Services
                        </Typography>
                        <Typography>
                          Enabling service sector cooperative ventures
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Container>
        </Box>
    );
}

export default Yuvasahakar
